import React, { useEffect, useRef } from "react";

interface Props {
  animationData: any[];
  className?: string;
  delay?: number;
  type?: number;
  lineWidth?: number;
  frameSpeed?: number;
  [key: string]: any;
}

export const FORWARD = 0;
export const REVERSE = 1;
export const FORWARD_REVERSE = 2;

function AnimationRunner({
  animationData,
  className,
  delay,
  type,
  lineWidth,
  frameSpeed = 1,
  ...props
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D>(null);

  const isRunning = useRef<boolean>(false);

  // Set function as empty function before useEffect to avoid error
  let checkIfInViewport = () => null;

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = contextRef.current;

    const { width, height } = canvas.getBoundingClientRect();
    [canvas.width, canvas.height] = [width * 2, height * 2];

    const scaling = width / 500;
    ctx.scale(scaling, scaling);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    contextRef.current = ctx;

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    ctx.strokeStyle = "#000000";

    resizeCanvas();

    let animationFrameId: number;
    let startFrame = 0;
    let endFrame = 0;
    let endAnimation = false;
    const maxFrame = animationData
      .map(line => line.length)
      .reduce((a, b) => a + b, 0);

    const drawFrame = (start, end) => {
      let currentAnimationData = [];

      let currentFrame = 0;
      for (let i = 0; i < animationData.length; ++i) {
        currentAnimationData.push([]);
        for (let j = 0; j < animationData[i].length; ++j) {
          if (currentFrame >= start && currentFrame <= end) {
            currentAnimationData[currentAnimationData.length - 1].push(
              animationData[i][j]
            );
          }
          currentFrame++;
        }
      }

      ctx.clearRect(0, 0, ctx.canvas.width * 50, ctx.canvas.height * 50);

      currentAnimationData.forEach(line => {
        ctx.beginPath();
        ctx.lineWidth = lineWidth || 10;
        ctx.lineCap = ctx.lineJoin = "round";
        line.forEach((point, i) => {
          const [x, y] = point;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.closePath();
      });
    };

    if (prefersReducedMotion) {
      drawFrame(0, maxFrame);
      return;
    }

    const animate = () => {
      drawFrame(startFrame, endFrame);

      switch (type) {
        case FORWARD:
          if (endFrame < maxFrame) endFrame += frameSpeed;
          else endAnimation = true;
          break;
        case FORWARD_REVERSE:
          if (endFrame < maxFrame) endFrame += frameSpeed;
          else if (startFrame < maxFrame) startFrame += frameSpeed;
          else {
            endFrame = 0;
            startFrame = 0;
          }
          break;
      }

      if (endAnimation) cancelAnimationFrame(animationFrameId);
      else animationFrameId = requestAnimationFrame(animate);
    };

    const isInViewport = () => {
      var { bottom } = canvas.getBoundingClientRect();
      return (
        bottom <= (window.innerHeight || document.documentElement.clientHeight)
      );
    };

    checkIfInViewport = () => {
      if (isInViewport() && !isRunning.current) {
        setTimeout(animate, delay);
        isRunning.current = true;
      }
    };

    // Check if the canvas is in viewport on load
    checkIfInViewport();
    // Check if the canvas is in viewport before starting animation
    window.addEventListener("scroll", checkIfInViewport);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("scroll", checkIfInViewport);
    };
  }, []);

  return (
    <div className={className + " pointer-events-none"} aria-hidden="true" role="presentation" {...props}>
      <canvas className="w-full h-full" ref={canvasRef}></canvas>
    </div>
  );
}

export const Animation = React.memo(AnimationRunner);
